import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Copy, ScanSearch } from "lucide-react";

const STATE_STYLES = {
  absent: "bg-red-100 text-red-700",
  generique: "bg-amber-100 text-amber-700",
  sur_optimise: "bg-orange-100 text-orange-700",
  decoratif: "bg-slate-100 text-slate-600",
  correct: "bg-green-100 text-green-700",
};

export function AltDiagnosticDrawer({ issue, siteId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.post("/seo-fix/alt-diagnostic", { site_id: siteId, page_url: issue.page_url })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Diagnostic impossible"))
      .finally(() => setLoading(false));
  }, [siteId, issue.page_url]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="alt-diagnostic-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full shadow-2xl p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-950 flex items-center gap-2">
            <ScanSearch className="w-4 h-4 text-indigo-600" /> Diagnostic ALT images
          </h2>
          <button onClick={onClose} data-testid="alt-drawer-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <a href={issue.page_url} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline break-all">{issue.page_url}</a>

        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="alt-manual-banner">
          <strong>Statut : Manuel requis</strong> — aucune écriture automatique des ALT (non prouvée fiable).
          Copiez la suggestion puis appliquez-la dans l'éditeur de votre CMS. Jamais de « Appliqué » ni « Vérifié ✓ » pour les ALT.
        </div>

        {loading && (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Analyse du HTML public + suggestions IA (données réelles uniquement)…
          </div>
        )}
        {error && <div className="mt-6 text-sm text-red-600" data-testid="alt-error">{error}</div>}

        {data && data.images.length === 0 && (
          <div className="mt-6 text-sm text-slate-500" data-testid="alt-empty">Aucune image détectée dans le HTML public de cette page.</div>
        )}

        {data && data.images.length > 0 && (
          <div className="mt-4 space-y-3">
            {data.images.map((im, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3" data-testid={`alt-image-row-${i}`}>
                <div className="flex items-start gap-3">
                  <img src={im.src} alt="" className="w-14 h-14 object-cover rounded border border-slate-200 flex-shrink-0 bg-slate-50"
                    onError={(e) => { e.target.style.visibility = "hidden"; }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-slate-600 truncate max-w-[200px]" title={im.src}>{im.filename || "(sans nom)"}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATE_STYLES[im.state] || STATE_STYLES.correct}`} data-testid={`alt-state-badge-${i}`}>
                        {im.state_label}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      ALT actuel : {im.alt_present ? (im.alt ? <span className="italic">« {im.alt} »</span> : <span className="font-mono">alt=""</span>) : <em>aucun attribut</em>}
                    </div>
                    {im.suggestion ? (
                      <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid={`alt-suggestion-${i}`}>
                        <div className="font-medium text-emerald-800">Suggestion : « {im.suggestion} »</div>
                        {im.reason && <div className="text-emerald-700 italic mt-0.5">{im.reason}</div>}
                        <button
                          onClick={() => { navigator.clipboard.writeText(im.suggestion); toast.success("ALT copié — collez-le dans l'éditeur Wix"); }}
                          data-testid={`alt-copy-button-${i}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline">
                          <Copy className="w-3 h-3" /> Copier
                        </button>
                      </div>
                    ) : im.reason ? (
                      <div className="mt-2 text-xs text-slate-600 italic" data-testid={`alt-reason-${i}`}>{im.reason}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <button onClick={onClose} className="border border-slate-300 rounded-md px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Fermer</button>
        </div>
      </div>
    </div>
  );
}
